import classNames from 'classnames';
import { ReactNode } from 'react';
import Button from '@/components/molecules/button/button';
import Column, { ColumnProps } from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Modal from '@/components/utilities/modal/modal';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './lightbox.module.scss';

export interface LightboxProps {
  /** The id of the element that names the lightbox, e.g. its heading */
  'aria-labelledby'?: string;
  /** The children to render in the lightbox. */
  children: ReactNode;
  /** The class name to apply */
  className?: string;
  /** Content rendered below the lightbox, peeking out from under it */
  footer?: ReactNode;
  /** The footer class name to apply */
  footerClassName?: string;
  /** Indicates whether to show lightbox in fullscreen */
  fullscreen?: boolean;
  /** Indicates whether the modal is centered in the screen */
  isCentered?: boolean;
  /** Whether the lightbox can be closed, e.g. false while saving. The close button stays focusable */
  isDismissible?: boolean;
  /** Indicates whether the lightbox should be full width on mobile */
  isFullWidthOnMobile?: boolean;
  /** Indicates whether the lightbox is open or not. */
  isOpen?: boolean;
  /** The callback to close the lightbox. */
  onClose: () => void;
  /** The size of the lightbox. */
  size?: 'large' | 'medium' | 'small';
  /** The theme of the modal */
  theme?: Exclude<Theme, null>;
  /** The wrapper class name to apply */
  wrapperClassName?: string;
}

const Lightbox = ({
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  footer,
  footerClassName,
  fullscreen = false,
  isCentered = false,
  isDismissible = true,
  isFullWidthOnMobile = false,
  isOpen,
  onClose,
  size = 'medium',
  theme = 'light',
  wrapperClassName,
}: LightboxProps) => {
  const { t } = useI18n();

  let columnOffset: ColumnProps['offset'];
  let columnWidth: ColumnProps['width'];

  switch (size) {
    case 'large':
      columnOffset = {};
      columnWidth = { mobile: 12 };
      break;

    case 'small':
      columnOffset = { tablet: 2 };
      columnWidth = { tablet: 8 };
      break;

    default:
      columnOffset = { desktop: 1, laptop: 2 };
      columnWidth = { desktop: 10, laptop: 8 };
  }

  if (!isOpen) return null;

  return (
    <Modal
      aria-labelledby={ariaLabelledBy}
      isCentered={isCentered}
      isDismissible={isDismissible}
      isOpen={isOpen}
      onClickOutside={onClose}
      setIsOpen={onClose}
    >
      <Container
        className={classNames(styles.component, className, {
          [styles.isLarge]: size === 'large',
          [styles.isFullscreen]: fullscreen,
          [styles.isFullWidthOnMobile]: isFullWidthOnMobile,
          [styles.hasFooter]: footer,
        })}
        theme={theme}
      >
        <Row className={styles.row}>
          <Column
            className={styles.column}
            offset={columnOffset}
            width={columnWidth}
          >
            <div className={classNames(styles.wrapper, wrapperClassName)}>
              <Button
                aria-label={t('items.common.words.close')}
                className={styles.close}
                disabled={!isDismissible}
                icon="Close"
                iconSize={24}
                onClick={onClose}
                variant="tertiary"
              />

              <div className={styles.inner}>{children}</div>
            </div>

            {footer && (
              <div className={classNames(styles.footer, footerClassName)}>
                {footer}
              </div>
            )}
          </Column>
        </Row>
      </Container>
    </Modal>
  );
};

Lightbox.displayName = 'Lightbox';

export default Lightbox;
